import { Box, Stack, Typography } from '@mui/material';
import BlendedPresenceRateCard from 'src/components/visibility/components/BlendedPresenceRateCard';
import SoloBidderRateCard, { SoloBidderWeeklyDatum } from 'src/components/visibility/components/SoloBidderRateCard';
import TopCompetitorsCard, { TopCompetitorStat } from 'src/components/visibility/components/TopCompetitorsCard';

interface WeeklyDataPoint {
  week_start: string;
  solo_bidder: number;
  top_ad_position: number;
  organic_only: number;
  compet_only: number;
}

interface CardBarProps {
  blendedPresenceTitle: string;
  blendedPresenceContent?: string;
  blendedPresenceTopMetric?: string;
  blendedPresenceWeeklySeries?: WeeklyDataPoint[];
  soloBidderTitle: string;
  /** Current solo bidder rate as number (e.g. 12 for 12%) */
  soloBidderValue?: number | null;
  soloBidderWeeklySeries?: SoloBidderWeeklyDatum[];
  topCompetitorsTitle: string;
  topCompetitorsData?: TopCompetitorStat[];
  topCompetitorsRightLabel?: string;
  dateRange?: string;
  loading?: boolean;
  selectedCountry?: string | null;
  /** Use same card wrapper + footer as non-brand page (copy/paste layout) */
  useNonBrandLayout?: boolean;
}

function CardFooter({ text }: { text: string }) {
  return (
    <Stack className="card-footer" width="100%">
      <Typography className="card-footer-text" variant="caption" color="text.secondary">
        {text}
      </Typography>
    </Stack>
  );
}

const nonBrandCardSx = {
  overflow: 'hidden' as const,
  width: '100%',
  maxHeight: 224,
  height: 224,
  borderRadius: 2,
  p: 0,
  pb: 0,
  px: 0,
  bgcolor: 'white',
  border: '1px solid rgba(145, 158, 171, 0.12)',
};

export function CardBar({
  blendedPresenceTitle,
  blendedPresenceContent,
  blendedPresenceTopMetric,
  blendedPresenceWeeklySeries,
  soloBidderTitle,
  soloBidderValue,
  soloBidderWeeklySeries,
  topCompetitorsTitle,
  topCompetitorsData,
  topCompetitorsRightLabel,
  dateRange = '12 weeks',
  loading = false,
  selectedCountry,
  useNonBrandLayout = false,
}: CardBarProps) {
  const footerText = `Last ${dateRange}`;

  if (useNonBrandLayout) {
    return (
      <Box sx={{ width: '100%' }}>
        <Stack direction="row" spacing={2} sx={{ width: '100%' }}>
          <Stack className="card-top-header-container" direction="column" sx={nonBrandCardSx}>
            <BlendedPresenceRateCard
              title={blendedPresenceTitle}
              mainContentTitle={blendedPresenceContent}
              dateRange={dateRange}
              topMetric={blendedPresenceTopMetric}
              weeklySeries={blendedPresenceWeeklySeries}
              loading={loading}
              contentOnly
            />
            <CardFooter text={footerText} />
          </Stack>
          <Stack className="card-top-header-container" direction="column" sx={nonBrandCardSx}>
            <SoloBidderRateCard
              title={soloBidderTitle}
              dateRange={dateRange}
              value={soloBidderValue}
              weeklySeries={soloBidderWeeklySeries}
              loading={loading}
              contentOnly
            />
            <CardFooter text={footerText} />
          </Stack>
          <Stack className="card-top-header-container" direction="column" sx={nonBrandCardSx}>
            <TopCompetitorsCard
              title={topCompetitorsTitle}
              dateRange={dateRange}
              competitors={topCompetitorsData}
              loading={loading}
              selectedCountry={selectedCountry}
              rightLabel={topCompetitorsRightLabel}
              contentOnly
            />
            <CardFooter text={footerText} />
          </Stack>
        </Stack>
      </Box>
    );
  }

  return (
    <Box sx={{ width: '100%' }}>
      <Stack direction="row" spacing={2} sx={{ width: '100%' }}>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <BlendedPresenceRateCard
            title={blendedPresenceTitle}
            mainContentTitle={blendedPresenceContent}
            dateRange={dateRange}
            topMetric={blendedPresenceTopMetric}
            weeklySeries={blendedPresenceWeeklySeries}
            loading={loading}
          />
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <SoloBidderRateCard
            title={soloBidderTitle}
            dateRange={dateRange}
            value={soloBidderValue}
            weeklySeries={soloBidderWeeklySeries}
            loading={loading}
          />
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <TopCompetitorsCard
            title={topCompetitorsTitle}
            dateRange={dateRange}
            competitors={topCompetitorsData}
            loading={loading}
            selectedCountry={selectedCountry}
          />
        </Box>
      </Stack>
    </Box>
  );
}


